import React from "react";
import Spinner from "./Spinner";

interface ButtonProps {
  type: "primary" | "secondary" | "outline" | "text";
  text?: string;
  children?: React.ReactNode;
  leadingIcon?: React.ReactElement;
  trailingIcon?: React.ReactElement;
  className?: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  isLoading?: boolean;
  disabled?: boolean;
  enableHover?: boolean;
}

const Button: React.FC<ButtonProps> = ({
  type,
  text,
  children,
  leadingIcon,
  trailingIcon,
  className = "",
  onClick,
  isLoading = false,
  disabled = false,
  enableHover = true,
}) => {
  const isButtonDisabled = isLoading || disabled;

  // Check if className contains text color utilities
  const hasCustomTextColor = className && /text-/.test(className);
  const hasCustomBgColor = className && /(bg-|hover:bg-)/.test(className);
  const hasCustomBorderColor = className && /border-/.test(className);

  const baseClass =
    "flex items-center py-2.5 px-5 border rounded-3xl transition-colors duration-300 text-sm";

  // Text type has no border, no bg, no padding from baseClass — it's a bare text button
  const textBaseClass = "flex items-center transition-colors duration-300 text-sm border-none bg-transparent p-0";

  // Primary style without text/bg colors if custom ones are provided
  const primaryStyle = enableHover
    ? `${hasCustomBgColor ? "" : "bg-[#EA33C0] hover:bg-[#1A1A1A]"} ${hasCustomTextColor ? "" : "text-white hover:text-white"}`
    : `${hasCustomBgColor ? "" : "bg-[#EA33C0]"} ${hasCustomTextColor ? "" : "text-white"}`;

  // Secondary style without text/bg colors if custom ones are provided
  const secondaryStyle = enableHover
    ? `${hasCustomBorderColor ? "" : "border-[#C72BA4]"} ${hasCustomTextColor ? "" : "text-black hover:text-white"} ${hasCustomBgColor ? "" : "hover:bg-[#EA33C0]"} group`
    : `${hasCustomBorderColor ? "" : "border-[#C72BA4]"} ${hasCustomTextColor ? "" : "text-black"} group`;

  // Outline style without text/bg colors if custom ones are provided
  const outlineStyle = enableHover
    ? `${hasCustomBorderColor ? "" : "border-white"} ${hasCustomTextColor ? "" : "text-white hover:text-black"} ${hasCustomBgColor ? "" : "hover:bg-white"} group`
    : `${hasCustomBorderColor ? "" : "border-white"} ${hasCustomTextColor ? "" : "text-white"} group`;

  // Text style — no border, no bg; caller controls color/decoration via className
  const textStyle = enableHover
    ? `${hasCustomTextColor ? "" : "text-gray-500 hover:text-gray-700"}`
    : `${hasCustomTextColor ? "" : "text-gray-500"}`;

  const disabledStyle = "opacity-50 cursor-not-allowed pointer-events-none";

  const styleByType =
    type === "primary"
      ? primaryStyle
      : type === "secondary"
        ? secondaryStyle
        : type === "outline"
          ? outlineStyle
          : textStyle;

  const resolvedBaseClass = type === "text" ? textBaseClass : baseClass;

  const buttonClass = `${resolvedBaseClass} ${styleByType} ${
    isButtonDisabled ? disabledStyle : ""
  } ${className}`
    .trim()
    .replace(/\s+/g, " ");

  const iconClass =
    type === "secondary"
      ? enableHover
        ? "text-[#EA33C0] group-hover:text-white transition-colors duration-300"
        : "text-[#EA33C0] transition-colors duration-300"
      : type === "outline"
        ? enableHover
          ? "text-white group-hover:text-black transition-colors duration-300"
          : "text-white transition-colors duration-300"
        : "";

  return (
    <button
      className={buttonClass}
      onClick={isButtonDisabled ? undefined : onClick}
      disabled={isButtonDisabled}
    >
      {leadingIcon && !isLoading && (
        <span className={`mr-2 ${iconClass}`}>{leadingIcon}</span>
      )}

      {isLoading ? (
        <Spinner size={20} />
      ) : (
        <>{children != null ? children : <span>{text}</span>}</>
      )}

      {trailingIcon && !isLoading && (
        <span className={`ml-2 ${iconClass}`}>{trailingIcon}</span>
      )}
    </button>
  );
};

export default Button;
